import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, Award, Users, Shield, BookOpen, Sparkles, Stethoscope,
  ArrowLeft, ArrowRight, CheckCircle, Star,
} from 'lucide-react';

const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);
const PRICE = '$9.99';
const PRICE_LABEL = '/month';

/**
 * Single combined "About Us / Mission Statement" page that covers BOTH
 * divisions of the association — MedCare National Board of Nursing AND
 * the Vital Health Sciences Association. One umbrella org, one $9.99/mo
 * membership.
 */
const _DIVISIONS = [
  {
    name: 'MedCare National Board of Nursing',
    short: 'Workforce-Development Credentialing Body',
    icon: Stethoscope,
    accent: '#0d9488',
    bg: '#ccfbf1',
    mission:
      'To provide a recognized workforce-development credentialing pathway for ' +
      'practical-nurse, CNA, EMT, phlebotomy, and allied-health graduates of ' +
      'USDOL-registered apprenticeship programs — and to keep them connected to ' +
      'continuing education, peer mentorship, and career-advancement resources ' +
      'throughout their working life.',
    pillars: [
      'Continuing-Education credit tracking',
      'Peer mentorship & study groups',
      'Career-services referrals',
      'Member directory + verifiable digital badge',
    ],
  },
  {
    name: 'Vital Health Sciences Association',
    short: 'Integrative & Holistic Practitioner Body',
    icon: Sparkles,
    accent: '#9333ea',
    bg: '#f3e8ff',
    mission:
      'To support non-licensed integrative, holistic, herbal, and natural-health ' +
      'practitioners with ethics-first standards of practice, continuing education, ' +
      'and a recognized credentialing path. We exist to keep wellness practice ' +
      'safe, evidence-aware, and clearly scoped — protecting both the public and ' +
      'the practitioner.',
    pillars: [
      'Code-of-ethics framework for non-licensed practice',
      'Scope-of-practice clarity & disclosure templates',
      'CE credits across all five doctorate tracks',
      'Member directory + verifiable digital badge',
    ],
  },
];

const _BENEFITS = [
  { icon: Award, title: 'Digital Membership Card', text: 'Downloadable PDF card with your photo, member number, and division stamp.' },
  { icon: Users, title: 'Public Member Directory', text: 'Optional public listing so employers and clients can verify you.' },
  { icon: BookOpen, title: 'CE Credits Tracker', text: 'Log continuing-education hours from any provider — keep them in one place.' },
  { icon: Star, title: '10% Member Discount', text: 'Save 10% on every future MedCare course enrollment.' },
  { icon: Shield, title: 'Members-Only Library', text: 'Extra study guides, NCLEX-PN/RN banks, and policy templates.' },
  { icon: Heart, title: 'Verifiable Digital Badge', text: 'LinkedIn-shareable URL that anyone can verify on our public endpoint.' },
];

const AssociationPage = () => {
  const [user, setUser] = useState(null);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const u = JSON.parse(stored);
        setUser(u);
        fetch(`${API}/api/membership/status/${u.id || u._id || ''}`)
          .then((r) => r.json())
          .then(setMembership)
          .catch(() => {});
      } catch { /* ignore */ }
    }
  }, []);

  const handleSubscribe = async () => {
    if (!user) {
      window.location.href = '/login?return=/association';
      return;
    }
    try {
      const res = await fetch(`${API}/api/membership/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id || user._id,
          email: user.email,
          full_name: user.name || user.full_name || user.email,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert('Could not start subscription. Please try again.');
      }
    } catch (e) {
      alert(`Subscription error: ${e.message}`);
    }
  };

  const isMember = membership?.is_member;

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' }}>
      {/* Top nav */}
      <div style={{ padding: '20px 40px', display: 'flex', gap: 20, alignItems: 'center' }}>
        <Link to="/" style={{ display: 'inline-flex', gap: 8, color: '#cbd5e1', textDecoration: 'none' }}>
          <ArrowLeft size={18} /> Back to Home
        </Link>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14 }}>
          <Link to="/medcare-board-of-nursing" style={{ color: '#5eead4', textDecoration: 'none' }}>MedCare Nursing Board</Link>
          <Link to="/vital-health-association" style={{ color: '#d8b4fe', textDecoration: 'none' }}>Vital Health Association</Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ padding: '40px 40px 60px', textAlign: 'center', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 999, background: 'rgba(212,175,55,0.16)', color: '#fde68a', fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 16 }}>
          About the Association
        </div>
        <h1 data-testid="association-hero-title" style={{ fontSize: '3rem', fontWeight: 800, margin: 0, lineHeight: 1.15, color: '#f8fafc' }}>
          One membership.<br />
          <span style={{ background: 'linear-gradient(90deg,#14b8a6,#a78bfa)', WebkitBackgroundClip: 'text', color: 'transparent' }}>Two professional bodies.</span>
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#cbd5e1', maxWidth: 760, margin: '20px auto 0', lineHeight: 1.6 }}>
          A single <strong style={{ color: '#fde68a' }}>{PRICE}{PRICE_LABEL}</strong> subscription
          grants you full membership in the <strong>MedCare National Board of Nursing</strong>
          {' '}<em>and</em> the <strong>Vital Health Sciences Association</strong> — covering every
          MedCare apprenticeship graduate from CMA through Doctor of Vital Health Sciences.
        </p>

        {/* CTA */}
        <div style={{ marginTop: 32, display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          {isMember ? (
            <Link
              to="/membership/dashboard"
              data-testid="association-go-to-dashboard"
              style={{ padding: '14px 28px', borderRadius: 999, background: 'linear-gradient(90deg,#14b8a6,#0d9488)', color: 'white', fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Go to Member Dashboard <ArrowRight size={18} />
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleSubscribe}
              data-testid="association-subscribe-btn"
              style={{ padding: '14px 32px', borderRadius: 999, background: 'linear-gradient(90deg,#14b8a6,#a78bfa)', color: 'white', fontWeight: 700, border: 0, cursor: 'pointer', fontSize: '1rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Join for {PRICE}{PRICE_LABEL} <ArrowRight size={18} />
            </button>
          )}
          <Link to="/login?return=/association" style={{ padding: '14px 28px', borderRadius: 999, background: 'transparent', color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.2)', textDecoration: 'none', fontWeight: 600 }}>
            Sign in
          </Link>
        </div>
        <p style={{ marginTop: 14, fontSize: '0.8rem', color: '#94a3b8' }}>
          Cancel anytime via Stripe Customer Portal. No long-term commitment.
        </p>
      </section>

      {/* Two-division card grid */}
      <section style={{ padding: '20px 40px 50px', maxWidth: 1100, margin: '0 auto' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: 24, color: '#f8fafc' }}>Mission of each division</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(420px,1fr))', gap: 22 }}>
          {_DIVISIONS.map((d) => {
            const Icon = d.icon;
            return (
              <div key={d.name} data-testid={`association-division-${d.name.split(' ')[0].toLowerCase()}`} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 26 }}>
                <div style={{ width: 50, height: 50, borderRadius: 12, background: d.bg, color: d.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Icon size={26} />
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc' }}>{d.name}</div>
                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8', marginTop: 4 }}>{d.short}</div>
                <p style={{ color: '#cbd5e1', marginTop: 12, lineHeight: 1.6 }}>{d.mission}</p>
                <ul style={{ marginTop: 14, padding: 0, listStyle: 'none' }}>
                  {d.pillars.map((p) => (
                    <li key={p} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#94a3b8', marginBottom: 6 }}>
                      <CheckCircle size={16} color={d.accent} style={{ marginTop: 3, flexShrink: 0 }} /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Member benefits */}
      <section style={{ padding: '40px 40px', maxWidth: 1100, margin: '0 auto', background: 'rgba(20,184,166,0.05)', borderRadius: 18 }}>
        <h2 style={{ fontSize: '1.5rem', textAlign: 'center', color: '#f8fafc', marginTop: 0 }}>
          What you get with your {PRICE}{PRICE_LABEL} membership
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 18, marginTop: 24 }}>
          {_BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} style={{ background: 'rgba(15,23,42,0.55)', borderRadius: 12, padding: 18, border: '1px solid rgba(255,255,255,0.06)' }}>
                <Icon size={22} color="#5eead4" />
                <div style={{ fontWeight: 700, color: '#f8fafc', marginTop: 8 }}>{b.title}</div>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: 4, lineHeight: 1.55 }}>{b.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Mission Statement */}
      <section style={{ padding: '60px 40px', maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: '1.5rem', color: '#f8fafc' }}>Mission statement</h2>
        <p style={{ color: '#cbd5e1', lineHeight: 1.75, fontSize: '1.05rem', marginTop: 18 }}>
          We exist to make the path from <em>workforce-development apprentice</em> to
          recognized practitioner faster, fairer, and clearer. We are a credentialing
          body for the apprenticeship era — verifying that our members have completed
          a USDOL-registered training program, then equipping them with the continuing
          education, ethics framework, peer community, and shareable digital credential
          they need to thrive throughout their career.
        </p>
        <p style={{ color: '#94a3b8', lineHeight: 1.7, marginTop: 16, fontSize: '0.95rem' }}>
          We are <strong>not</strong> a State Board of Nursing or a State Department
          of Health. Membership does not by itself confer a license to practice.
          Members must still meet each state&apos;s separate licensure requirements
          (NCLEX-PN/RN, CNA registry, EMT National Registry, etc.). Our role is to
          stand alongside those public bodies — never replace them.
        </p>
      </section>

      {/* Final CTA */}
      <section style={{ padding: '0 40px 80px', maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
        {isMember ? (
          <Link to="/membership/dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '16px 36px', borderRadius: 999, background: 'linear-gradient(90deg,#14b8a6,#0d9488)', color: 'white', fontWeight: 700, textDecoration: 'none', fontSize: '1.05rem' }}>
            Manage Your Membership <ArrowRight size={20} />
          </Link>
        ) : (
          <button
            type="button"
            onClick={handleSubscribe}
            data-testid="association-subscribe-btn-bottom"
            style={{ padding: '16px 36px', borderRadius: 999, background: 'linear-gradient(90deg,#14b8a6,#a78bfa)', color: 'white', fontWeight: 700, border: 0, cursor: 'pointer', fontSize: '1.05rem', display: 'inline-flex', alignItems: 'center', gap: 10 }}
          >
            Become a Member — {PRICE}{PRICE_LABEL} <ArrowRight size={20} />
          </button>
        )}
      </section>
      <div style={{ padding: '24px 40px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
        MedCare Nursing Institute &amp; Training Academy · 3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201
      </div>
    </div>
  );
};

export default AssociationPage;
